import { useState, useEffect, useRef } from "react";
import { saunaServices } from "@/data/sauna-services";
const saunaNavImages: Record<string, string> = { "advise-design": "sauna-detail-1.webp", supply: "sauna-supply-delivery.webp", install: "sauna-assembly.webp", "maintenance-repair": "sauna-detail-3.webp" };
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/hooks/use-analytics";
import { 
  Phone, 
  Menu, 
  X,
  ChevronDown,
  ArrowRight
} from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { LocationsDesktopPanel, LocationsMobileList } from "./locations-menu";
import logoSrc from "/images/brand/vertex-logo.png";
import imgEngineer from "@assets/GFXToolz.ai-GPTIMAGE-1775010051_1776018924252.png";
const imgVentPipes = `${import.meta.env.BASE_URL}images/ventilation-installation.webp`;
const imgComponents = `${import.meta.env.BASE_URL}images/ductwork-installation.webp`;
const imgCompressors = `${import.meta.env.BASE_URL}images/heating-daikin.webp`;

type MenuKey = "ac" | "hvac" | "sauna" | "locations";
type NavCard = { title: string; desc: string; href: string; img: string; testId: string };

const triggerBase = "flex items-center gap-1 text-[0.8125rem] xl:text-sm font-bold tracking-[-0.005em] hover:text-foreground hover:bg-primary/[0.06] transition-colors px-2.5 xl:px-3 py-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function MegaCard({ card, index, onNavigate }: { card: NavCard; index: number; onNavigate: () => void }) {
  return (
    <li className="h-full">
      <Link
        href={card.href}
        onClick={onNavigate}
        data-testid={card.testId}
        className="group relative flex h-full flex-col rounded-2xl bg-card p-2 ring-1 ring-border/80 shadow-[0_1px_2px_hsl(var(--foreground)/0.04)] motion-safe:transition-all duration-300 hover:ring-primary/40 hover:shadow-[0_18px_40px_-22px_hsl(var(--primary)/0.55)] motion-safe:hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span className="relative block aspect-[16/10] overflow-hidden rounded-xl bg-secondary">
          <img src={card.img} alt="" loading="lazy" className="h-full w-full object-cover motion-safe:transition-transform duration-700 ease-out motion-safe:group-hover:scale-[1.06]" />
          <span className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--foreground)/0.55)] via-[hsl(var(--foreground)/0.05)] to-transparent" aria-hidden />
          <span className="absolute left-3 top-3 rounded-full bg-background/90 px-2 py-0.5 font-mono text-[10px] font-semibold tabular-nums tracking-wider text-foreground/70 backdrop-blur-sm" aria-hidden>
            {String(index + 1).padStart(2, "0")}
          </span>
        </span>
        <span className="flex flex-1 flex-col px-2.5 pb-2.5 pt-3.5">
          <span className="flex items-start justify-between gap-3">
            <span className="font-display text-[15px] font-bold uppercase leading-snug text-foreground group-hover:text-primary transition-colors">{card.title}</span>
            <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/[0.08] text-primary motion-safe:transition-all duration-300 group-hover:bg-primary group-hover:text-primary-foreground group-focus-visible:bg-primary group-focus-visible:text-primary-foreground" aria-hidden>
              <ArrowRight className="h-3.5 w-3.5 motion-safe:transition-transform duration-300 motion-safe:group-hover:-rotate-45" />
            </span>
          </span>
          <span className="mt-1.5 block text-[13px] leading-relaxed text-muted-foreground">{card.desc}</span>
        </span>
      </Link>
    </li>
  );
}

function MegaPanel({ eyebrow, title, viewAll, cards, cols, onNavigate }: {
  eyebrow: string; title: string; viewAll?: { href: string; label: string; testId: string }; cards: NavCard[]; cols: 3 | 4; onNavigate: () => void;
}) {
  return (
    <div className="w-full px-4 lg:px-8 py-7">
      <div className="grid grid-cols-[220px_1fr] xl:grid-cols-[250px_1fr] gap-8">
        <div className="flex flex-col rounded-2xl bg-[hsl(var(--vx-blue))] p-5 text-white">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80">{eyebrow}</p>
          <h3 className="mt-2 font-display text-2xl font-bold uppercase leading-tight tracking-tight text-white">{title}</h3>
          <span className="mt-3 mb-6 block h-px w-10 bg-white/60" aria-hidden />
          {viewAll && (
            <Link
              href={viewAll.href}
              onClick={onNavigate}
              data-testid={viewAll.testId}
              className="group mt-auto inline-flex w-fit items-center gap-2 rounded-full border border-white/30 bg-white px-4 py-2 text-sm font-semibold text-[hsl(var(--vx-blue))] hover:bg-sky-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--vx-blue))]"
            >
              {viewAll.label} <ArrowRight className="h-4 w-4 motion-safe:transition-transform group-hover:translate-x-0.5" aria-hidden />
            </Link>
          )}
        </div>
        <ul className={`grid gap-4 ${cols === 4 ? "grid-cols-4" : "grid-cols-3"}`}>
          {cards.map((c, i) => <MegaCard key={c.href} card={c} index={i} onNavigate={onNavigate} />)}
        </ul>
      </div>
    </div>
  );
}

export function Header() {
  const headerRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef<Partial<Record<MenuKey, HTMLButtonElement | null>>>({});
  const closeTimer = useRef<number | null>(null);
  const pinnedMenu = useRef<MenuKey | null>(null);
  const [headerBottom, setHeaderBottom] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<MenuKey | null>(null);
  const [mobileAcOpen, setMobileAcOpen] = useState(false);
  const [mobileHvacOpen, setMobileHvacOpen] = useState(false);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);
  const [mobileSaunaOpen, setMobileSaunaOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const [location] = useLocation();

  const cancelClose = () => {
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const openMenu = (key: MenuKey) => {
    cancelClose();
    if (pinnedMenu.current !== key) pinnedMenu.current = null;
    setActiveMenu(key);
  };
  const scheduleClose = () => {
    cancelClose();
    if (pinnedMenu.current) return;
    closeTimer.current = window.setTimeout(() => setActiveMenu(null), 220);
  };
  const closeMenus = () => { cancelClose(); pinnedMenu.current = null; setActiveMenu(null); };
  const toggleMenu = (key: MenuKey) => {
    cancelClose();
    if (pinnedMenu.current === key) closeMenus();
    else { pinnedMenu.current = key; setActiveMenu(key); }
  };

  // Close every menu after navigation
  useEffect(() => {
    closeMenus();
    setMobileMenuOpen(false);
    setMobileSaunaOpen(false);
    setMobileLocationsOpen(false);
  }, [location]);

  useEffect(() => () => cancelClose(), []);

  // Escape closes (returning focus to trigger); click outside nav + panel closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      cancelClose();
      pinnedMenu.current = null;
      setActiveMenu((m) => { if (m) triggerRefs.current[m]?.focus(); return null; });
      setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!activeMenu) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!navRef.current?.contains(t) && !panelRef.current?.contains(t)) closeMenus();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [activeMenu]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setHeaderBottom(headerRef.current?.getBoundingClientRect().bottom ?? 0);
    };
    handleScroll();
    const observer = new ResizeObserver(handleScroll);
    if (headerRef.current) observer.observe(headerRef.current);
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const acServices = [
    { title: "Installation", desc: "Bespoke system design and precise installation for homes and businesses.", href: "/installation", img: `${import.meta.env.BASE_URL}images/home-installation.webp` },
    { title: "Repairs", desc: "Rapid diagnostics and durable repairs by fully qualified engineers.", href: "/repair", img: imgEngineer },
    { title: "Maintenance", desc: "Preventative servicing contracts to ensure efficiency and air quality.", href: "/maintenance", img: `${import.meta.env.BASE_URL}images/project-17.webp` }
  ];

  const hvacServices = [
    { title: "Ventilation", desc: "MVHR, MEV and commercial extract systems for healthy indoor air.", href: "/hvac/ventilation", img: imgVentPipes },
    { title: "Heating", desc: "Heat pumps, underfloor heating and modern heating solutions.", href: "/hvac/heating", img: imgCompressors },
    { title: "Ductwork", desc: "Custom fabrication, installation and cleaning of duct systems.", href: "/hvac/ductwork", img: imgComponents },
    { title: "BMS & Controls", desc: "Intelligent building automation and energy management systems.", href: "/hvac/controls", img: `${import.meta.env.BASE_URL}images/bms-controls.webp` }
  ];

  const acCards: NavCard[] = acServices.map((s) => ({ ...s, testId: `link-nav-ac-${s.href.slice(1)}` }));
  const hvacCards: NavCard[] = hvacServices.map((s) => ({ ...s, testId: `link-nav-hvac-${s.href.split("/").pop()}` }));
  const saunaCards: NavCard[] = saunaServices.map((s) => ({
    title: s.navTitle, desc: s.navDesc, href: s.href,
    img: `${import.meta.env.BASE_URL}images/${saunaNavImages[s.slug]}`, testId: `link-nav-sauna-${s.slug}`,
  }));

  const menus: { key: MenuKey; label: string; id: string; testId: string }[] = [
    { key: "ac", label: "Air Conditioning", id: "ac-menu", testId: "button-nav-ac" },
    { key: "hvac", label: "HVAC Systems", id: "hvac-menu", testId: "button-nav-hvac" },
    { key: "sauna", label: "Sauna", id: "sauna-menu", testId: "button-nav-sauna" },
    { key: "locations", label: "Locations", id: "locations-menu", testId: "button-nav-locations" },
  ];
  const activeId = menus.find((m) => m.key === activeMenu)?.id;

  return (
    <>
      <header ref={headerRef} className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'glass-nav py-3 shadow-[0_1px_0_hsl(var(--border)),0_8px_24px_-16px_hsl(var(--foreground)/0.25)]' : 'bg-background/95 py-5 border-b border-border/60'}`}>
        <div className="w-full px-4 lg:px-8 flex items-center justify-between">
          <Link href="/">
            <div className="flex items-center cursor-pointer" data-testid="link-home">
              <img src={logoSrc} alt="VertexAir Climate Systems" className="h-12 w-auto" />
            </div>
          </Link>

          <nav ref={navRef} className="hidden lg:flex flex-1 justify-end items-center gap-0.5 xl:gap-1 [&>div:first-child]:ml-auto">
            {menus.map((m) => {
              const open = activeMenu === m.key;
              return (
                <div key={m.key} className="py-5 -my-5" onMouseEnter={() => openMenu(m.key)} onMouseLeave={scheduleClose}>
                  <button
                    ref={(el) => { triggerRefs.current[m.key] = el; }}
                    type="button"
                    aria-expanded={open}
                    aria-controls={m.id}
                    aria-haspopup="true"
                    onClick={() => toggleMenu(m.key)}
                    className={`${triggerBase} ${open ? 'text-primary bg-primary/[0.06]' : 'text-foreground/75'}`}
                    data-testid={m.testId}
                  >
                    {m.label} <ChevronDown className={`w-3.5 h-3.5 opacity-70 motion-safe:transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              );
            })}

            <Link href="/blog" onMouseEnter={scheduleClose} className={`${triggerBase} ${location === '/blog' ? 'text-primary bg-primary/[0.06]' : 'text-foreground/75'}`} data-testid="link-nav-blog">Blog</Link>
            <Link href="/contact" onMouseEnter={scheduleClose} className={`${triggerBase} ${location === '/contact' ? 'text-primary bg-primary/[0.06]' : 'text-foreground/75'}`} data-testid="link-nav-contact">Contact</Link>
            
            <div className="flex items-center gap-2 xl:gap-3 ml-auto pl-3 xl:pl-5 border-l border-border/70">
              <a href="tel:02080870906" className="flex items-center gap-2 text-sm text-foreground font-semibold tabular-nums tracking-tight rounded-full px-2 py-2 hover:text-primary transition-colors" data-testid="link-nav-phone" onClick={() => trackEvent("call_click", "Header Phone", "/")}>
                <span className="grid place-items-center w-8 h-8 rounded-full bg-primary/10 ring-1 ring-primary/15"><Phone className="w-3.5 h-3.5 text-primary" aria-hidden /></span>
                <span className="sr-only xl:not-sr-only">020 8087 0906</span>
              </a>
              <Link href="/quote">
                <Button className="group rounded-full h-10 pl-4 pr-3.5 xl:pl-5 xl:pr-4 text-sm font-semibold tracking-tight shadow-[0_8px_20px_-10px_hsl(var(--primary)/0.7)] hover:shadow-[0_10px_24px_-10px_hsl(var(--primary)/0.8)] transition-all" data-testid="button-nav-quote">
                  Get a Free Quote <ArrowRight className="w-3.5 h-3.5 ml-1 motion-safe:transition-transform group-hover:translate-x-0.5" aria-hidden />
                </Button>
              </Link>
            </div>
          </nav>

          <button className="lg:hidden grid place-items-center w-10 h-10 rounded-full border border-border/70 bg-background/70 hover:bg-primary/[0.06] transition-colors" aria-expanded={mobileMenuOpen} aria-label={mobileMenuOpen ? "Close menu" : "Open menu"} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="button-mobile-menu">
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {activeMenu && (
          <motion.div
            ref={panelRef}
            key="desktop-mega"
            id={activeId}
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={{ duration: reduceMotion ? 0.1 : 0.18, ease: "easeOut" }}
            className="hidden lg:block fixed left-0 right-0 z-40 bg-background/[0.97] backdrop-blur-md border-b border-border shadow-[0_30px_60px_-30px_hsl(var(--foreground)/0.35)] max-h-[calc(100dvh-var(--hb))] overflow-y-auto"
            style={{ top: headerBottom, ["--hb" as string]: `${headerBottom}px` }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onBlur={(e) => {
              const next = e.relatedTarget as Node | null;
              if (next && !e.currentTarget.contains(next) && !navRef.current?.contains(next)) closeMenus();
            }}
          >
            <span className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" aria-hidden />
            <motion.div key={activeMenu} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}>
              {activeMenu === "ac" && (
                <MegaPanel eyebrow="Cooling & comfort" title="Air Conditioning Services" cols={3} cards={acCards} onNavigate={closeMenus}
                  viewAll={{ href: "/installation", label: "View all AC services", testId: "link-nav-ac-all" }} />
              )}
              {activeMenu === "hvac" && (
                <MegaPanel eyebrow="Air, heat & control" title="HVAC Systems" cols={4} cards={hvacCards} onNavigate={closeMenus}
                  viewAll={{ href: "/hvac", label: "View all HVAC services", testId: "link-nav-hvac-all" }} />
              )}
              {activeMenu === "sauna" && (
                <MegaPanel eyebrow="Heat & wellbeing" title="Sauna Services" cols={4} cards={saunaCards} onNavigate={closeMenus} />
              )}
              {activeMenu === "locations" && <LocationsDesktopPanel onNavigate={closeMenus} />}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-b border-border px-4 py-6 shadow-lg fixed w-full z-40 overflow-y-auto"
            style={{ top: headerBottom, maxHeight: `calc(100dvh - ${headerBottom}px)` }}
          >
            <div className="flex flex-col gap-2">
              <div className="border-b border-border/50 pb-4">
                <button 
                  onClick={() => setMobileAcOpen(!mobileAcOpen)} 
                  className="flex items-center justify-between w-full text-lg font-semibold text-foreground py-2"
                >
                  Air Conditioning <ChevronDown className={`w-5 h-5 transition-transform ${mobileAcOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileAcOpen && (
                  <div className="flex flex-col gap-3 mt-2">
                    {acServices.map((service, idx) => (
                      <Link key={idx} href={service.href}>
                        <div className="flex items-center gap-3 cursor-pointer py-1 rounded-lg hover:bg-slate-50 px-2" onClick={() => setMobileMenuOpen(false)}>
                          <img src={service.img} alt={service.title} className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <span className="font-semibold uppercase block">{service.title}</span>
                            <span className="text-xs text-muted-foreground">{service.desc}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-b border-border/50 pb-4">
                <button 
                  onClick={() => setMobileHvacOpen(!mobileHvacOpen)} 
                  className="flex items-center justify-between w-full text-lg font-semibold text-foreground py-2"
                >
                  HVAC Systems <ChevronDown className={`w-5 h-5 transition-transform ${mobileHvacOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileHvacOpen && (
                  <div className="flex flex-col gap-3 mt-2">
                    {hvacServices.map((service, idx) => (
                      <Link key={idx} href={service.href}>
                        <div className="flex items-center gap-3 cursor-pointer py-1 rounded-lg hover:bg-slate-50 px-2" onClick={() => setMobileMenuOpen(false)}>
                          <img src={service.img} alt={service.title} className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <span className="font-semibold uppercase block">{service.title}</span>
                            <span className="text-xs text-muted-foreground">{service.desc}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-b border-border/50 pb-4">
                <button
                  type="button"
                  aria-expanded={mobileSaunaOpen}
                  aria-controls="mobile-sauna-menu"
                  onClick={() => setMobileSaunaOpen(!mobileSaunaOpen)}
                  className="flex items-center justify-between w-full text-lg font-semibold text-foreground py-2"
                  data-testid="button-mobile-sauna"
                >
                  Sauna <ChevronDown className={`w-5 h-5 motion-safe:transition-transform ${mobileSaunaOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileSaunaOpen && (
                  <ul id="mobile-sauna-menu" className="flex flex-col gap-1 mt-2">
                    {saunaServices.map((s) => (
                      <li key={s.slug}>
                        <Link href={s.href} onClick={() => setMobileMenuOpen(false)} data-testid={`link-mobile-sauna-${s.slug}`}
                          className="flex gap-3 rounded-lg px-2 py-2 hover:bg-secondary">
                          <img src={`${import.meta.env.BASE_URL}images/${saunaNavImages[s.slug]}`} alt="" className="w-14 h-14 shrink-0 rounded-lg object-cover" />
                          <span>
                            <span className="font-semibold uppercase block">{s.navTitle}</span>
                            <span className="text-xs text-muted-foreground">{s.navDesc}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="border-b border-border/50 pb-4">
                <button
                  type="button"
                  aria-expanded={mobileLocationsOpen}
                  aria-controls="mobile-locations-menu"
                  onClick={() => setMobileLocationsOpen(!mobileLocationsOpen)}
                  className="flex items-center justify-between w-full text-lg font-semibold text-foreground py-2"
                  data-testid="button-mobile-locations"
                >
                  Locations <ChevronDown className={`w-5 h-5 motion-safe:transition-transform ${mobileLocationsOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileLocationsOpen && (
                  <LocationsMobileList onNavigate={() => { setMobileMenuOpen(false); setMobileLocationsOpen(false); }} />
                )}
              </div>

              <Link href="/blog" onClick={() => setMobileMenuOpen(false)} className={`text-lg font-semibold py-2 border-b border-border/50 block ${location === '/blog' ? 'text-primary' : ''}`} data-testid="link-mobile-blog">Blog</Link>
              <Link href="/contact"><span className="text-lg font-semibold py-2 border-b border-border/50 block cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Contact</span></Link>
              
              <div className="mt-4 flex flex-col gap-4">
                <a href="tel:02080870906" className="flex items-center gap-2 text-lg font-semibold bg-secondary p-3 rounded-lg justify-center" data-testid="link-mobile-phone" onClick={() => trackEvent("call_click", "Mobile Menu Phone")}>
                  <Phone className="w-5 h-5 text-primary" />
                  020 8087 0906
                </a>
                <Link href="/quote" className="w-full">
                  <Button size="lg" className="w-full text-lg" data-testid="button-mobile-quote">
                    Get a Free Quote
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
